// src/modules/portal/pages/ClaimProfilePage.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MagicLinkService, MagicLinkToken } from '../../../core/services/auth/MagicLinkService';
import { Ghost, ShieldCheck, Zap, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

export const ClaimProfilePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [tokenData, setTokenData] = useState<MagicLinkToken | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const checkToken = async () => {
      if (!token) {
        setError('Token de convite não fornecido.');
        setLoading(false);
        return;
      }

      try {
        const data = await MagicLinkService.verifyToken(token);
        if (!data) {
          setError('Este convite é inválido ou já expirou.');
        } else {
          setTokenData(data);
        }
      } catch (err) {
        console.error('Error verifying token:', err);
        setError('Ocorreu um erro ao verificar seu convite.');
      } finally {
        setLoading(false);
      }
    };

    checkToken();
  }, [token]);

  const handleClaim = async () => {
    if (!token) return;
    setVerifying(true);
    try {
      // Simulate the claiming process
      await MagicLinkService.markAsUsed(token);
      
      // Artificial delay for UX
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSuccess(true);
      
      // Refresh or redirect after a few seconds
      setTimeout(() => {
        navigate(`/${tokenData?.targetId}`);
      }, 3000);
    } catch (err) {
      console.error('Error claiming profile:', err);
      setError('Falha ao reivindicar o perfil. Tente novamente mais tarde.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-midnight">
        <Loader2 className="animate-spin text-brand-gold" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-midnight p-6">
        <div className="max-w-md w-full bg-brand-charcoal/30 border border-white/10 rounded-3xl p-8 text-center backdrop-blur-3xl">
          <AlertCircle className="mx-auto text-red-500 mb-6" size={64} />
          <h1 className="text-3xl font-black text-white mb-4 font-display uppercase tracking-tight">Convite Inválido</h1>
          <p className="text-white/40 mb-8 font-medium italic">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full bg-white/5 border border-white/10 text-white/40 py-4 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white/10 transition-all font-display"
          >
            Voltar para a Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-midnight p-6">
        <div className="max-w-md w-full bg-brand-charcoal/30 border border-brand-gold/20 rounded-3xl p-8 text-center backdrop-blur-3xl animate-in fade-in zoom-in duration-500 shadow-glow-gold/5">
          <div className="w-20 h-20 bg-brand-gold/10 rounded-full flex items-center justify-center mx-auto mb-8 shadow-glow-gold/10">
            <CheckCircle2 className="text-brand-gold" size={48} />
          </div>
          <h1 className="text-3xl font-black text-white mb-4 font-display uppercase tracking-tight">Perfil Reivindicado!</h1>
          <p className="text-white/40 mb-8 font-medium">
            Parabéns! Você agora tem controle total sobre seu perfil na Nossa Pesca.
          </p>
          <p className="text-[10px] text-brand-gold font-black uppercase tracking-[0.2em] animate-pulse">
            Redirecionando você para seu novo perfil oficial...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-glow-mesh-dark opacity-30"></div>
      
      {/* Branding Header */}
      <div className="mb-16 text-center animate-in slide-in-from-top duration-700 relative z-10">
        <div className="text-6xl mb-6">🎣</div>
        <h1 className="text-5xl font-black text-white tracking-tighter font-display uppercase">Nossa Pesca.com</h1>
        <p className="text-brand-gold font-black uppercase tracking-[0.3em] text-[10px] mt-2">Plataforma Oficial de Turismo de Pesca</p>
      </div>

      <div className="max-w-2xl w-full bg-brand-charcoal/30 border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-700 relative z-10 backdrop-blur-3xl">
        <div className="bg-brand-gold px-8 py-5 text-center">
          <p className="text-brand-midnight font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2">
            <Zap size={16} fill="currentColor" /> Convite Especial de Ativação
          </p>
        </div>

        <div className="p-8 md:p-14">
          <div className="flex flex-col md:flex-row gap-10 items-center md:items-start">
            <div className="w-32 h-32 bg-white/5 rounded-3xl flex items-center justify-center border-2 border-dashed border-white/10 relative group overflow-hidden shrink-0">
               <Ghost className="text-white/10 transition-transform group-hover:scale-110 duration-500" size={64} />
               <div className="absolute inset-0 bg-brand-gold/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            <div className="flex-1 text-center md:text-left">
              <h2 className="text-4xl font-black text-white mb-3 font-display uppercase tracking-tight">@{tokenData?.targetId}</h2>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-8">
                <span className="bg-brand-gold/10 text-brand-gold border border-brand-gold/20 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-glow-gold/10">
                  Shadow Profile Detectado
                </span>
                <span className="flex items-center gap-2 text-white/20 text-[10px] font-black uppercase tracking-widest">
                  <ShieldCheck size={14} className="text-brand-gold" /> 100% Seguro
                </span>
              </div>
              
              <p className="text-white/40 text-lg mb-10 leading-relaxed font-medium">
                Nossa IA identificou seu negócio e preparou um perfil exclusivo com suas fotos e dados públicos.
                <span className="text-white font-black"> Reivindicar este perfil é gratuito</span> e permite que você receba leads diretos de pescadores de todo o Brasil.
              </p>

              <button 
                onClick={handleClaim}
                disabled={verifying}
                className="w-full bg-brand-orange text-white px-8 py-5 rounded-2xl font-black text-xl hover:scale-[1.02] transition-all shadow-glow-orange active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-3 group font-display uppercase tracking-widest"
              >
                {verifying ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  <>
                    REIVINDICAR PERFIL
                    <Zap className="group-hover:translate-x-1 transition-transform" size={24} />
                  </>
                )}
              </button>

              <p className="mt-8 text-center text-[10px] text-white/20 font-black uppercase tracking-widest">
                Ao clicar, você confirma que é o proprietário da @{tokenData?.targetId}.
                A verificação será solicitada no próximo passo.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 p-10 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="font-black text-brand-gold text-3xl mb-1 font-display">+15k</div>
            <div className="text-[10px] text-white/20 font-black uppercase tracking-widest">Acessos Mensais</div>
          </div>
          <div className="text-center">
            <div className="font-black text-brand-gold text-3xl mb-1 font-display">Top 1</div>
            <div className="text-[10px] text-white/20 font-black uppercase tracking-widest">Ranking SEO Pesca</div>
          </div>
          <div className="text-center">
            <div className="font-black text-brand-orange text-3xl mb-1 font-display">Grátis</div>
            <div className="text-[10px] text-white/20 font-black uppercase tracking-widest">Taxa de Adesão</div>
          </div>
        </div>
      </div>
    </div>
  );
};
